'use client';

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { X } from 'lucide-react';
import Checkbox from '@/components/ui/Checkbox/Checkbox';
import { A2_BLOG_URL, A2_BLOG_TITLE } from '@/utils/links';

const STORAGE_KEY = 't3k_a2_modal_seen';

/**
 * Returns true if the user has previously closed the First-A2 modal with the
 * "Don't show me this again" checkbox checked. Returns false during SSR.
 */
export function hasDismissedFirstA2Modal(): boolean {
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem(STORAGE_KEY) === '1';
}

/**
 * Whether the First-A2 modal should be shown on an upcoming A2 download.
 * Unlike a "show once" pattern, this checks the persistent dismissal flag —
 * the user has to explicitly opt out via the checkbox to silence it.
 */
export function shouldShowFirstA2Modal(): boolean {
  return !hasDismissedFirstA2Modal();
}

interface Props {
  open: boolean;
  onClose: () => void;
}

export const FirstA2DownloadModal = ({ open, onClose }: Props) => {
  // Default the checkbox to checked, per design — most users dismiss without
  // wanting to see this again, and the affirmative-checked-by-default makes
  // closing-equals-dismissing the path of least resistance.
  const [dontShowAgain, setDontShowAgain] = useState(true);
  const modalRef = useRef<HTMLDivElement>(null);

  const handleClose = () => {
    if (dontShowAgain) {
      window.localStorage.setItem(STORAGE_KEY, '1');
    }
    onClose();
  };

  useEffect(() => {
    if (!open) return;
    const handleOutsideClick = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        handleClose();
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, dontShowAgain]);

  // Reset the checkbox to its default whenever the modal re-opens so each
  // appearance starts from the same baseline (matches the dismissal contract:
  // the user must opt out *this time* to silence future opens).
  useEffect(() => {
    if (open) setDontShowAgain(true);
  }, [open]);

  if (!open) return null;

  return (
    <span className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-40">
      <div
        ref={modalRef}
        className="bg-zinc-900 border border-zinc-500 border-opacity-50 text-white w-full max-w-md rounded-xl shadow-xl flex flex-col"
      >
        <div className="flex justify-between items-center p-4">
          <span className="text-xl font-semibold">Your first A2 model</span>
          <button onClick={handleClose} className="text-gray-400 hover:text-white" aria-label="Close">
            <X size={24} />
          </button>
        </div>
        <div className="p-4 pt-0 flex flex-col gap-4">
          <p className="text-zinc-400 text-base">
            This capture uses Architecture 2 (A2), the latest NAM standard. It&apos;s
            designed to sound better and work on more devices.
          </p>
          <p className="text-zinc-400 text-base">
            Make sure your hardware or software supports A2 before loading.
          </p>
          <p className="text-zinc-400 text-base">
            Learn More:{' '}
            <Link
              href={A2_BLOG_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-400 hover:underline"
            >
              {A2_BLOG_TITLE}
            </Link>
          </p>
          <div className="pt-2">
            <Checkbox
              label="Don't show me this again"
              checked={dontShowAgain}
              onChange={setDontShowAgain}
            />
          </div>
        </div>
      </div>
    </span>
  );
};
